import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GripVertical, LoaderCircle, Pencil, Plus, RefreshCw, Search, Trash2, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { hasPermission } from "../lib/utils/permissions";
import { useAuthStore } from "../context/AuthContext";
import useToast from "../hooks/useToast";
import apiClient from "../lib/utils/network-client";
import { DeleteConfirmationButton } from "../components/common/ConfirmationDialog";
import useDragAutoScroll from "../hooks/useDragAutoScroll";

const normalizeSearchText = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const Specialities = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { showErrorToast, showSuccessToast } = useToast();
  const canCreate = hasPermission(user, "specialities", "create");
  const canEdit = hasPermission(user, "specialities", "edit");
  const [specialities, setSpecialities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [draggedSpecialityId, setDraggedSpecialityId] = useState(null);
  const [dropTargetSpecialityId, setDropTargetSpecialityId] = useState(null);
  const [isReordering, setIsReordering] = useState(false);
  const [updatingVisibilityIds, setUpdatingVisibilityIds] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedVisibility, setSelectedVisibility] = useState("all");

  const hasActiveFilters = selectedCategory !== "all" || selectedVisibility !== "all";
  const hasReorderConstraints = Boolean(search.trim()) || hasActiveFilters;

  const filteredSpecialities = useMemo(() => {
    const query = normalizeSearchText(search);

    return specialities.filter((item) => {
      if (selectedCategory !== "all" && item.category !== selectedCategory) {
        return false;
      }

      if (selectedVisibility !== "all") {
        const shouldBeVisible = selectedVisibility === "visible";
        if (Boolean(item.show_on_home) !== shouldBeVisible) {
          return false;
        }
      }

      if (!query) {
        return true;
      }

      const searchableContent = [
        item.title,
        item.slug,
        item.sub_description,
        item.category,
        item.description,
        item.services_heading,
        ...(item.services || []).map((service) => service.title),
        ...(item.departments || []).flatMap((department) => [
          department.name,
          department.description,
        ]),
      ];

      return normalizeSearchText(searchableContent.join(" ")).includes(query);
    });
  }, [search, selectedCategory, selectedVisibility, specialities]);

  const sortedSpecialities = useMemo(
    () =>
      [...filteredSpecialities].sort((a, b) => {
        if ((a.display_order ?? 0) !== (b.display_order ?? 0)) {
          return (a.display_order ?? 0) - (b.display_order ?? 0);
        }
        return (a.title || "").localeCompare(b.title || "");
      }),
    [filteredSpecialities],
  );

  const canReorder = canEdit && !hasReorderConstraints && !isLoading && !isReordering && sortedSpecialities.length > 1;
  const { handleDragAutoScroll, stopDragAutoScroll } = useDragAutoScroll(
    Boolean(draggedSpecialityId),
    canReorder,
  );

  const fetchSpecialities = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get("/specialities");
      setSpecialities(response.data?.data?.specialities || []);
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to load specialities");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSpecialities();
  }, []);

  const handleClearFilters = () => {
    setSearch("");
    setSelectedCategory("all");
    setSelectedVisibility("all");
  };

  const handleDelete = async (item) => {
    try {
      await apiClient.delete(`/specialities/${item.id}`);
      showSuccessToast("Speciality deleted successfully");
      fetchSpecialities();
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Unable to delete speciality");
    }
  };

  const handleVisibilityChange = async (item) => {
    if (!canEdit || updatingVisibilityIds.includes(item.id)) return;

    const nextVisibility = !Boolean(item.show_on_home);
    setSpecialities((current) =>
      current.map((speciality) =>
        speciality.id === item.id
          ? { ...speciality, show_on_home: nextVisibility }
          : speciality,
      ),
    );
    setUpdatingVisibilityIds((current) => [...current, item.id]);

    try {
      await apiClient.put(`/specialities/${item.id}`, {
        show_on_home: nextVisibility,
      });
      showSuccessToast(
        nextVisibility
          ? "Speciality is now visible on the homepage"
          : "Speciality hidden from the homepage",
      );
    } catch (err) {
      setSpecialities((current) =>
        current.map((speciality) =>
          speciality.id === item.id
            ? { ...speciality, show_on_home: item.show_on_home }
            : speciality,
        ),
      );
      showErrorToast(err.response?.data?.message || "Unable to update homepage visibility");
    } finally {
      setUpdatingVisibilityIds((current) => current.filter((id) => id !== item.id));
    }
  };

  const persistSpecialityOrder = async (orderedSpecialities, previousSpecialities) => {
    const normalizedSpecialities = orderedSpecialities.map((item, index) => ({
      ...item,
      display_order: index + 1,
    }));

    setSpecialities(normalizedSpecialities);
    setIsReordering(true);

    try {
      await apiClient.put("/specialities/reorder", {
        orderedItems: normalizedSpecialities.map((item) => ({
          id: item.id,
          display_order: item.display_order,
        })),
      });
      showSuccessToast("Speciality order updated successfully");
    } catch (err) {
      setSpecialities(previousSpecialities);
      showErrorToast(err.response?.data?.message || "Unable to update speciality order");
    } finally {
      setIsReordering(false);
      setDraggedSpecialityId(null);
      setDropTargetSpecialityId(null);
    }
  };

  const handleDropSpeciality = async (event, targetSpecialityId) => {
    event.preventDefault();
    stopDragAutoScroll();

    if (!canReorder || !draggedSpecialityId || draggedSpecialityId === targetSpecialityId) {
      setDraggedSpecialityId(null);
      setDropTargetSpecialityId(null);
      return;
    }

    const draggedIndex = sortedSpecialities.findIndex((item) => item.id === draggedSpecialityId);
    const targetIndex = sortedSpecialities.findIndex((item) => item.id === targetSpecialityId);
    if (draggedIndex < 0 || targetIndex < 0) {
      setDraggedSpecialityId(null);
      setDropTargetSpecialityId(null);
      return;
    }

    const previousSpecialities = [...specialities];
    const reorderedSpecialities = [...sortedSpecialities];
    const [movedSpeciality] = reorderedSpecialities.splice(draggedIndex, 1);
    reorderedSpecialities.splice(targetIndex, 0, movedSpeciality);
    await persistSpecialityOrder(reorderedSpecialities, previousSpecialities);
  };

  return (
    <div
      className="max-w-[1600px] space-y-6"
      onDragOver={handleDragAutoScroll}
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Specialities</h1>
          <p className="mt-1 text-sm font-medium text-slate-500">Manage speciality content and homepage visibility.</p>
          {canEdit ? (
            <p className={`mt-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] ${hasReorderConstraints ? "text-amber-600" : "text-slate-400"}`}>
              {isReordering ? (
                <>
                  <LoaderCircle className="h-3.5 w-3.5 animate-spin" />
                  Saving speciality order
                </>
              ) : hasReorderConstraints ? (
                "Clear search and filters to reorder specialities"
              ) : (
                "Drag the handle to reorder; hold near the top or bottom to auto-scroll"
              )}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-3">
          <Button type="button" variant="outline" className="rounded-lg" onClick={fetchSpecialities}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          {canCreate && (

            <Button type="button" className="rounded-lg" onClick={() => navigate("/specialities/new")}>
            <Plus className="mr-2 h-4 w-4" />
            Add Speciality
          </Button>

          )}
        </div>
      </div>

      <div className="rounded-lg border border-white/60 bg-white/80 p-5 shadow-sm backdrop-blur-xl">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-[minmax(320px,2fr)_minmax(190px,1fr)_minmax(210px,1fr)_auto] xl:items-end">
          <div className="space-y-1.5">
            <label htmlFor="speciality-search" className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
              Search
            </label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                id="speciality-search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search title, service, department or content"
                className="h-11 rounded-lg pl-10"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
              Category
            </label>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger aria-label="Filter specialities by category">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="general">General Specialities</SelectItem>
                <SelectItem value="super">Super Specialities</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
              Homepage Visibility
            </label>
            <Select value={selectedVisibility} onValueChange={setSelectedVisibility}>
              <SelectTrigger aria-label="Filter specialities by homepage visibility">
                <SelectValue placeholder="All Visibility" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Visibility</SelectItem>
                <SelectItem value="visible">Visible on Homepage</SelectItem>
                <SelectItem value="hidden">Hidden from Homepage</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button
            type="button"
            variant="ghost"
            className="h-11 justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            onClick={handleClearFilters}
            disabled={!hasReorderConstraints}
          >
            <X className="mr-2 h-4 w-4" />
            Clear
          </Button>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-4 text-sm">
          <p className="font-semibold text-slate-600">
            Showing {sortedSpecialities.length} of {specialities.length} specialities
          </p>
          {hasReorderConstraints && canEdit ? (
            <p className="text-xs font-medium text-amber-600">
              Reordering is paused while search or filters are active.
            </p>
          ) : null}
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-white/60 bg-white/80 shadow-sm backdrop-blur-xl">
        {isLoading ? (
          <div className="py-12 text-center text-sm font-medium text-slate-500">Loading specialities...</div>
        ) : sortedSpecialities.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <p className="text-sm font-semibold text-slate-700">No specialities match your search and filters.</p>
            <Button
              type="button"
              variant="ghost"
              className="mt-3 rounded-lg text-primary"
              onClick={handleClearFilters}
            >
              <X className="mr-2 h-4 w-4" />
              Clear search and filters
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50/80">
                <tr>
                  <th className="w-14 px-3 py-4 text-center">
                    <span className="sr-only">Reorder</span>
                  </th>
                  <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Speciality</th>
                  <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Category</th>
                  <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Visible on Homepage</th>
                  <th className="px-5 py-4 text-right text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sortedSpecialities.map((item) => (
                  <tr
                    key={item.id}
                    onDragOver={(event) => {
                      if (!canReorder || !draggedSpecialityId) return;
                      event.preventDefault();
                      event.dataTransfer.dropEffect = "move";
                      if (draggedSpecialityId !== item.id) {
                        setDropTargetSpecialityId(item.id);
                      }
                    }}
                    onDrop={(event) => handleDropSpeciality(event, item.id)}
                    className={`transition-all duration-200 ${
                      draggedSpecialityId === item.id
                        ? "bg-primary/5 opacity-50"
                        : dropTargetSpecialityId === item.id
                          ? "bg-primary/5 ring-2 ring-inset ring-primary/20"
                          : "hover:bg-slate-50/60"
                    }`}
                  >
                    <td className="w-14 px-3 py-4 text-center">
                      <span
                        role="button"
                        tabIndex={canReorder ? 0 : -1}
                        draggable={canReorder}
                        aria-label={`Drag to reorder ${item.title}`}
                        title="Drag to reorder"
                        onDragStart={(event) => {
                          if (!canReorder) {
                            event.preventDefault();
                            return;
                          }
                          event.dataTransfer.effectAllowed = "move";
                          event.dataTransfer.setData("text/plain", item.id);
                          setDraggedSpecialityId(item.id);
                        }}
                        onDragEnd={() => {
                          stopDragAutoScroll();
                          setDraggedSpecialityId(null);
                          setDropTargetSpecialityId(null);
                        }}
                        className={`inline-flex h-9 w-9 items-center justify-center rounded-lg border transition-colors ${
                          canReorder
                            ? "cursor-grab border-slate-200 bg-white text-slate-500 hover:border-primary/40 hover:bg-primary/5 hover:text-primary active:cursor-grabbing"
                            : "cursor-not-allowed border-slate-100 bg-slate-50 text-slate-300"
                        }`}
                      >
                        {isReordering && draggedSpecialityId === item.id ? (
                          <LoaderCircle className="h-4 w-4 animate-spin" />
                        ) : (
                          <GripVertical className="h-4 w-4" />
                        )}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div>
                        <div className="text-sm font-semibold text-slate-800">{item.title}</div>
                        <div className="line-clamp-2 text-sm text-slate-500">{item.sub_description}</div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm capitalize text-slate-600">{item.category}</td>
                    <td className="px-5 py-4">
                      <label className={`inline-flex items-center gap-3 ${canEdit ? "cursor-pointer" : "cursor-not-allowed opacity-60"}`}>
                        <input
                          type="checkbox"
                          checked={Boolean(item.show_on_home)}
                          disabled={!canEdit || updatingVisibilityIds.includes(item.id)}
                          onChange={() => handleVisibilityChange(item)}
                          aria-label={`${item.show_on_home ? "Hide" : "Show"} ${item.title} on the homepage`}
                          className="peer sr-only"
                        />
                        <span className="relative h-6 w-11 shrink-0 rounded-full bg-slate-300 transition-colors after:absolute after:left-0.5 after:top-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow-sm after:transition-transform after:content-[''] peer-checked:bg-primary peer-checked:after:translate-x-5 peer-focus-visible:ring-2 peer-focus-visible:ring-primary/30 peer-focus-visible:ring-offset-2 peer-disabled:opacity-60" />
                        <span className="text-sm font-medium text-slate-600">
                          {updatingVisibilityIds.includes(item.id)
                            ? "Saving..."
                            : item.show_on_home
                              ? "Visible"
                              : "Hidden"}
                        </span>
                      </label>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" className="rounded-lg" onClick={() => navigate(`/specialities/${item.id}/edit`)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Edit
                        </Button>
                        <DeleteConfirmationButton onConfirm={() => handleDelete(item)}>
<Button type="button" variant="outline" className="rounded-lg border-red-200 text-red-600 hover:bg-red-50">
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </Button>
</DeleteConfirmationButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Specialities;
